/**
 * Funções PURAS das duas grids de títulos: NovaTelaDeTitulos.vue (rota Contas a Receber) e
 * TelaDeTitulos.vue (rota Contas a Pagar).
 *
 * Só entra aqui o que não usa `this`, store nem API. As duas telas continuam sendo dois
 * componentes separados de propósito, porque atendem rotas diferentes: o que elas têm em
 * comum é a formatação abaixo, e nada mais.
 */

/**
 * Formata um valor em moeda como as duas grids já formatavam, e devolve string vazia quando
 * não há valor.
 *
 * ARMADILHA: o `|| ''` só pega null e undefined (que o optional chaining transforma em
 * undefined). O zero NÃO cai nele, porque 0 formatado é 'R$ 0,00', que é truthy. Trocar o
 * `||` por `??` mudaria o que a grid mostra.
 *
 * O locale é 'pt-br' em minúsculo, igual estava nas duas telas.
 */
export function formatarMoedaOuVazio(valor: number | null | undefined): string {
	return valor?.toLocaleString('pt-br', {
		style: 'currency',
		currency: 'BRL',
	}) || ''
}

/**
 * Corta a descrição do título para caber na coluna: quebra no último espaço do trecho cortado
 * quando existe um, senão corta no meio da palavra. Corpo copiado de NovaTelaDeTitulos.vue.
 */
export function truncarDescricaoDoTitulo(
	descricaoCompleta: string,
	maxDescriptionLength: number,
): string {
	let descricao = descricaoCompleta
	if (descricao.length > maxDescriptionLength) {
		const truncated = descricao.substring(0, maxDescriptionLength)
		const lastSpaceIndex = truncated.lastIndexOf(' ')
		if (lastSpaceIndex > 0) {
			descricao = truncated.substring(0, lastSpaceIndex) + '...'
		} else {
			descricao = truncated + '...'
		}
	}
	return descricao
}

/**
 * Rótulo da forma de pagamento na grid de Contas a Receber (NovaTelaDeTitulos.vue).
 *
 * ARMADILHA: esta lista e a de Contas a Pagar (logo abaixo) NÃO são iguais, e o motivo da
 * diferença não está claro. Só esta mapeia 'LINK' e 'CHEQUE'; só a de Contas a Pagar mapeia
 * 'PERMUTA'. Unificar as duas mudaria o rótulo que aparece em uma das telas, então elas ficam
 * duplicadas de propósito, uma do lado da outra, para a divergência ficar visível.
 *
 * Forma não mapeada devolve undefined, e a coluna fica vazia.
 */
export function formatarFormaDePagamentoDeContasAReceber(formaDePagamento: string): string | undefined {
	let formaFormatada
	if (formaDePagamento == 'CARTAO') formaFormatada = 'Cartão'
	if (formaDePagamento == 'DINHEIRO') formaFormatada = 'Dinheiro'
	if (formaDePagamento == 'VOUCHER') formaFormatada = 'Voucher'
	if (formaDePagamento == 'OUTROS') formaFormatada = 'Outros'
	if (formaDePagamento == 'TEF CREDITO') formaFormatada = 'TEF Crédito'
	if (formaDePagamento == 'TEF DEBITO') formaFormatada = 'TEF Débito'
	if (formaDePagamento == 'CREDIARIO') formaFormatada = 'Crediário'
	if (formaDePagamento == 'LINK') formaFormatada = 'Link'
	if (formaDePagamento == 'CHEQUE') formaFormatada = 'Cheque'
	if (formaDePagamento == 'BOLETO') formaFormatada = 'Boleto'
	if (formaDePagamento == 'PIX') formaFormatada = 'Pix'
	return formaFormatada
}

/**
 * Rótulo da forma de pagamento na grid de Contas a Pagar (TelaDeTitulos.vue). Ver a armadilha
 * documentada na função de Contas a Receber: as duas listas divergem.
 */
export function formatarFormaDePagamentoDeContasAPagar(formaDePagamento: string): string | undefined {
	let formaFormatada
	if (formaDePagamento == 'CARTAO') formaFormatada = 'Cartão'
	if (formaDePagamento == 'DINHEIRO') formaFormatada = 'Dinheiro'
	if (formaDePagamento == 'VOUCHER') formaFormatada = 'Voucher'
	if (formaDePagamento == 'OUTROS') formaFormatada = 'Outros'
	if (formaDePagamento == 'TEF CREDITO') formaFormatada = 'TEF Crédito'
	if (formaDePagamento == 'TEF DEBITO') formaFormatada = 'TEF Débito'
	if (formaDePagamento == 'CREDIARIO') formaFormatada = 'Crediário'
	if (formaDePagamento == 'BOLETO') formaFormatada = 'Boleto'
	if (formaDePagamento == 'PIX') formaFormatada = 'Pix'
	if (formaDePagamento == 'PERMUTA') formaFormatada = 'Permuta'
	return formaFormatada
}
